import animate from './animate';

/**
 * Scroll an HTML element in to view
 * Opinionated - will scroll the element to the viewport (window) center
 * @param {HTMLElement} element the HTML element to calculate position
 * @param {Function} onStart the callback when animation starts
 * @param {Function} onComplete the callback when animation completes
 */
/* istanbul ignore next */
export function scrollIntoView<T extends HTMLElement = HTMLElement>(
  element?: T | null,
  onStart: () => void = () => {},
  onComplete: () => void = () => {}
): void {
  if (!element) return;
  animate({
    fromValue: window.scrollY,
    toValue: element.offsetTop - window.innerHeight / 2 + element.offsetHeight / 2,
    onStart,
    onUpdate: (scrollTop: number, next: () => void) => {
      window.scrollTo({ left: 0, top: scrollTop });
      next();
    },
    onComplete,
  });
}

/**
 * Determine if a range intersects a point
 * @param {number} p the intersection point
 * @param {number} y1 the range start point
 * @param {number} y2  the range end point
 */
export function intersects(p: number, y1: number, y2: number): boolean {
  return y1 <= p && y2 > p;
}

export const focusableSelectors = [
  'a[href]',
  'button',
  'textarea',
  'input:not([type="hidden"])',
  'select',
  '[tabindex]:not([tabindex="-1"])',
];
export const focusableFilter = (el: Element): boolean =>
  !el.hasAttribute('disabled') && !el.hasAttribute('aria-hidden');

export function findFirstFocusable(el: HTMLElement): HTMLElement | undefined {
  const elements = el.querySelectorAll(focusableSelectors.join(','));
  return Array.from(elements).filter(focusableFilter)[0] as HTMLElement;
}

export default {
  intersects,
  scrollIntoView,
  focusableFilter,
  findFirstFocusable,
};
